import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { AccountConfiguration } from "@shared/types";
import { HedgeMode } from "@shared/features/configuration/types";
import { skipToken } from "@reduxjs/toolkit/query";

interface AccountConfigurationProviderProps {
  accountId?: number;
  isPreview?: boolean;
}

const AccountConfigurationProvider = ({
  accountId,
  isPreview,
  children,
}: PropsWithChildren<AccountConfigurationProviderProps>) => {
  const { data, isFetching, isLoading } =
    sharedLib.sharedApi.configuration.useGetAccountConfigurationQuery(
      accountId ? { account_id: accountId } : skipToken
    );
  const [hedgeMode, setHedgeMode] = useState<HedgeMode | undefined>();

  const originalData = useMemo(() => {
    return {
      hedgeMode: data?.is_hedge_mode
        ? HedgeMode.HEDGE_MODE
        : HedgeMode.ONE_WAY_MODE,
    };
  }, [data]);

  const hasPendingChanges = hedgeMode !== originalData.hedgeMode;

  useEffect(() => {
    if (originalData) {
      setHedgeMode(originalData.hedgeMode);
    }
  }, [originalData]);

  return (
    <AccountConfigurationContext.Provider
      value={{
        data,
        isFetching,
        isLoading,
        hedgeMode,
        setHedgeMode,
        isPreview,
        accountId,
        hasPendingChanges,
      }}
    >
      {children}
    </AccountConfigurationContext.Provider>
  );
};

export default AccountConfigurationProvider;

export const AccountConfigurationContext = React.createContext<
  | {
      data: AccountConfiguration | undefined;
      isFetching: boolean;
      isLoading: boolean;
      hedgeMode?: HedgeMode;
      setHedgeMode?: (hedgeMode?: HedgeMode) => void;
      isPreview?: boolean;
      accountId?: number;
      hasPendingChanges?: boolean;
    }
  | undefined
>(undefined);
